import { CommonModule, JsonPipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../shared/utility.service';
import { GroupField } from '../../shared/dynamic-forms.interface2';
import { Dosage } from 'fhir/r4';

interface TimingCodeOption {
    code: string;
    display: string;
    system?: string;
}

interface DurationUnitOption {
    code: string;
    label: string;
}

interface DosageFormValue {
    dosageTiming: {
        timingCode: TimingCodeOption | string | null;
        duration: number | null;
        durationUnit: string | null;
    } | null;
    doseAndRateDoseQuantity: {
        value: number | null;
        unit: string | null;
    } | null;
    dosageInstruction: string | null;
}

@Component({
    selector: 'app-medication-dosage-validator',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        JsonPipe
    ],
    templateUrl: './medication-dosage-validator.component.html',
    styleUrl: './medication-dosage-validator.component.scss'
})
export class MedicationDosageValidatorComponent implements OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly utility = inject(UtilityService);
    private readonly formSub = new Subscription();

    private readonly dosageGroups = this.getDosageGroups();
    private readonly timingGroup = this.dosageGroups.find(group => group.generalProperties.fieldApiName === 'dosageTiming');
    private readonly quantityGroup = this.dosageGroups.find(group => group.generalProperties.fieldApiName === 'doseAndRateDoseQuantity');

    readonly timingCodeOptions: TimingCodeOption[] = this.extractTimingCodeOptions();
    readonly durationUnitOptions: DurationUnitOption[] = this.buildDurationUnitOptions();
    readonly quantityUnitHint = this.quantityGroup?.groupFields?.['unit']?.generalProperties?.fieldPlaceholder ?? 'mg, ml, etc.';
    readonly timingHint = this.timingGroup?.generalProperties?.groupFieldsHint ?? 'When to take the medication';

    readonly dosageForm = this.fb.group({
        dosageTiming: this.fb.group({
            timingCode: this.fb.control<TimingCodeOption | string | null>(null),
            duration: this.fb.control<number | null>(null),
            durationUnit: this.fb.control<string | null>(null)
        }),
        doseAndRateDoseQuantity: this.fb.group({
            value: this.fb.control<number | null>(null),
            unit: this.fb.control<string | null>(null)
        }),
        dosageInstruction: this.fb.control<string>('')
    });

    dosagePreview: Dosage | null = null;
    readonly otherDispenseFields = [
        {
            name: 'quantity',
            description: 'SimpleQuantity showing the amount actually dispensed (e.g., 30 tablets).'
        },
        {
            name: 'daysSupply',
            description: 'SimpleQuantity summarising how long the supply should last (pharmacy obligation).'
        },
        {
            name: 'whenPrepared / whenHandedOver',
            description: 'Timestamps documenting when the dispense was prepared and handed to the patient.'
        },
        {
            name: 'destination / receiver',
            description: 'Logistics trail showing where the medication went and who received it.'
        },
        {
            name: 'substitution',
            description: 'Records whether a substitution occurred plus reasons (v3-SubstanceAdminSubstitutionReason).'
        },
        {
            name: 'authorizingPrescription',
            description: 'References the source MedicationRequest(s) tied to this dispense event.'
        }
    ];

    constructor() {
        this.formSub.add(
            this.dosageForm.valueChanges.subscribe(value => this.updatePreview(value as DosageFormValue))
        );
    }

    ngOnDestroy(): void {
        this.formSub.unsubscribe();
    }

    loadExample(preset: 'daily' | 'taper'): void {
        const timingCode = preset === 'daily'
            ? this.findTimingCode('BID')
            : this.findTimingCode('QOD');

        const durationUnit = this.durationUnitOptions.find(unit => unit.code === (preset === 'daily' ? 'd' : 'wk'))?.label ?? null;

        if (preset === 'daily') {
            this.dosageForm.patchValue({
                dosageTiming: {
                    timingCode,
                    duration: 7,
                    durationUnit,
                },
                doseAndRateDoseQuantity: {
                    value: 500,
                    unit: 'mg'
                },
                dosageInstruction: 'Take 500 mg twice a day for one week.'
            });
        } else {
            this.dosageForm.patchValue({
                dosageTiming: {
                    timingCode,
                    duration: 2,
                    durationUnit,
                },
                doseAndRateDoseQuantity: {
                    value: 1,
                    unit: 'tablet'
                },
                dosageInstruction: 'Take one tablet every other day for two weeks.'
            });
        }
    }

    resetForm(): void {
        this.dosageForm.reset();
        this.dosagePreview = null;
    }

    private updatePreview(value: DosageFormValue): void {
        this.dosagePreview = this.utility.constructDosageTypeObject(value);
    }

    private getDosageGroups(): GroupField[] {
        const raw = this.utility.commonFormFields['dosage'];
        return Array.isArray(raw)
            ? raw.filter((field): field is GroupField => !!field.generalProperties?.isGroup)
            : raw
                ? [raw as GroupField]
                : [];
    }

    private extractTimingCodeOptions(): TimingCodeOption[] {
        const timingField = this.timingGroup?.groupFields?.['timingCode'];
        const data = (timingField as any)?.data;
        if (!Array.isArray(data)) {
            return [];
        }
        return data.map(option => ({
            code: option.code,
            display: option.display,
            system: option.system
        }));
    }

    private buildDurationUnitOptions(): DurationUnitOption[] {
        return this.utility.durationUnits?.map(unit => ({
            code: unit[0],
            label: unit[1] ?? unit[0]
        })) ?? [];
    }

    private findTimingCode(code: string): TimingCodeOption | null {
        return this.timingCodeOptions.find(option => option.code === code) ?? null;
    }
}
